import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { notification } from "antd";
import { useNavigate } from "react-router-dom";
import {
  IoCalendarOutline,
  IoGameControllerOutline,
  IoPeopleOutline,
  IoPersonOutline,
  IoTimeOutline,
  IoTrophy,
  IoGiftOutline,
  IoMicOffOutline,
  IoMicOutline,
  IoVolumeHighOutline,
  IoVolumeMuteOutline,
} from "react-icons/io5";
import { resolveAvatarSrc } from "../../utils/avatarUrl";
import ChampionshipChat from "../../components/championship/ChampionshipChat";
import useChampionshipLive from "../../components/championship/useChampionshipLive";
import "./championship.css";

const ROUND_TITLES = {
  final: "FINAL",
  semifinal: "SEMIFINALES",
  quarterfinal: "CUARTOS DE FINAL",
  roundOf16: "OCTAVOS DE FINAL",
};
const ROUND_SHORT = {
  final: "F",
  semifinal: "SF",
  quarterfinal: "CF",
  roundOf16: "OF",
};
const MODE_LABELS = {
  ciencia: "Ciencia",
  azar: "Azar",
  cienzar: "Cienzar",
  contrazar: "Contrazar",
};

const TIME_OPTIONS = [1, 2, 3, 5, 10, 15, 20, 30, 60];

const pad = (value) => String(value).padStart(2, "0");

const formatSchedule = (value) => {
  if (!value) return "Por definir";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Por definir";
  return `${pad(date.getDate())}/${pad(date.getMonth() + 1)} - ${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

// Value for <input type="datetime-local"> in the viewer's local time.
const toLocalInputValue = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(
    date.getMinutes(),
  )}`;
};

const formatCountdown = (target, now) => {
  if (!target) return "";
  const diff = new Date(target).getTime() - now;
  if (!Number.isFinite(diff)) return "";
  if (diff <= 0) return "En juego / por jugar";
  const totalSeconds = Math.floor(diff / 1000);
  const days = Math.floor(totalSeconds / 86400);
  const hours = Math.floor((totalSeconds % 86400) / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  return days > 0
    ? `${days}d ${pad(hours)}:${pad(minutes)}:${pad(seconds)}`
    : `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`;
};

const entryLabel = (entry, roundKeyById) => {
  if (!entry) return "—";
  if (entry.kind === "player") return entry.name;
  if (entry.kind === "bot") return "Bot XIMA";
  if (entry.kind === "open") return `Jugador ${entry.seat}`;
  if (entry.kind === "winner") {
    const short = ROUND_SHORT[roundKeyById[entry.matchId]] || "R";
    return `Ganador ${short} ${entry.index}`;
  }
  return "—";
};

const PlayerAvatar = ({ entry, size = "md" }) => {
  const src = entry?.kind === "player" ? resolveAvatarSrc(entry.avatarUrl || "") : "";
  const initials =
    entry?.kind === "bot"
      ? "🤖"
      : entry?.kind === "player"
      ? String(entry.name || "?").trim().slice(0, 2).toUpperCase()
      : "?";
  return (
    <span className={`xc-avatar xc-avatar-${size}${entry?.kind === "open" ? " is-open" : ""}`}>
      {src ? <img src={src} alt="" /> : initials}
    </span>
  );
};

const Championship = () => {
  const navigate = useNavigate();
  const live = useChampionshipLive();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [calendarTab, setCalendarTab] = useState("upcoming");
  const [editingMatchId, setEditingMatchId] = useState("");
  const [draftSchedule, setDraftSchedule] = useState("");
  const [draftWinner, setDraftWinner] = useState("");
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [draftSettings, setDraftSettings] = useState(null);
  const [now, setNow] = useState(() => Date.now());

  const showError = (error, fallback) =>
    notification.error({ message: error?.response?.data?.message || fallback });

  const load = useCallback(async () => {
    try {
      const response = await axios.get("/championship");
      setData(response.data);
    } catch (error) {
      showError(error, "No se pudo cargar el campeonato.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const refresh = window.setInterval(load, 30000);
    return () => window.clearInterval(refresh);
  }, [load]);

  useEffect(() => {
    const tick = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(tick);
  }, []);

  const runAction = async (request, fallback) => {
    if (busy) return;
    setBusy(true);
    try {
      const response = await request();
      setData(response.data);
      return true;
    } catch (error) {
      showError(error, fallback);
      return false;
    } finally {
      setBusy(false);
    }
  };

  const rounds = useMemo(() => data?.rounds || [], [data]);
  const roundKeyById = useMemo(() => {
    const map = {};
    rounds.forEach((round) => round.matches.forEach((match) => (map[match.id] = round.key)));
    return map;
  }, [rounds]);
  // Final on top, first round at the bottom.
  const roundsTopDown = useMemo(() => [...rounds].reverse(), [rounds]);
  const firstRound = rounds[0]?.matches || [];
  const seatEntries = firstRound.flatMap((match) => [match.a, match.b]);

  const players = data?.players || [];
  const me = String(data?.me || "");
  const isMyMatch = (match) =>
    !match.winner && [match.a, match.b].some((entry) => entry?.kind === "player" && entry.userId === me);
  const myOpenMatch = rounds.flatMap((round) => round.matches).find((match) => isMyMatch(match));

  const enterMyMatch = async (match) => {
    if (busy) return;
    setBusy(true);
    try {
      const response = await axios.post(`/championship/matches/${match.id}/enter`);
      const tournamentId = String(response?.data?.tournamentId || "");
      await axios.post("/tournaments/join", { id: tournamentId });
      navigate(`/game/${tournamentId}?autoplay=1`, { state: { fromLobbySource: "championship" } });
    } catch (error) {
      showError(error, "No se pudo entrar al partido.");
    } finally {
      setBusy(false);
    }
  };

  const openReplay = (historyId, match) => {
    navigate("/game/history", {
      state: {
        openReplayRequestKey: `championship:${historyId}:${Date.now()}`,
        openReplayMatch: {
          id: historyId,
          historyId,
          hasReplay: true,
          tournamentName: data?.title || "Campeonato XIMA",
          participants: [match.a, match.b]
            .filter(Boolean)
            .map((entry) => ({ displayName: entry.name || (entry.kind === "bot" ? "Bot XIMA" : "") })),
        },
      },
    });
  };

  // Tocar un partido: en vivo se mira, terminado se reproduce.
  const handleMatchClick = (match) => {
    if (match.live && match.tournamentId && match.roomId) {
      navigate(
        `/game/${match.tournamentId}?autoplay=1&spectator=1&roomId=${encodeURIComponent(match.roomId)}`,
      );
      return;
    }
    const historyId = match.replayId || match.historyIds?.[match.historyIds.length - 1];
    if (historyId) openReplay(historyId, match);
  };
  const isAdmin = Boolean(data?.isAdmin);
  const isRegistration = data?.status === "registration";

  const startEditing = (match) => {
    if (!isAdmin) return;
    if (editingMatchId === match.id) {
      setEditingMatchId("");
      return;
    }
    setEditingMatchId(match.id);
    setDraftSchedule(toLocalInputValue(match.scheduledAt));
    setDraftWinner(match.winner || "");
  };

  const saveMatch = async (match) => {
    const saved = await runAction(
      () =>
        axios.put(`/championship/admin/matches/${match.id}`, {
          scheduledAt: draftSchedule ? new Date(draftSchedule).toISOString() : null,
          winner: draftWinner,
        }),
      "No se pudo guardar el partido.",
    );
    if (saved) setEditingMatchId("");
  };

  const openSettings = () => {
    setDraftSettings({
      title: data?.title || "Campeonato XIMA",
      prize: data?.prize || "",
      gameMode: data?.gameMode || "ciencia",
      timeMinutes: data?.timeMinutes || 5,
      maxPlayers: data?.maxPlayers || 12,
      status: data?.status || "registration",
    });
    setSettingsOpen((open) => !open);
  };

  const saveSettings = async () => {
    const saved = await runAction(
      () => axios.put("/championship/admin/settings", draftSettings),
      "No se pudo guardar el campeonato.",
    );
    if (saved) setSettingsOpen(false);
  };

  const resetChampionship = () => {
    if (!window.confirm("¿Reiniciar el campeonato? Se borran los inscritos, horarios y resultados.")) return;
    runAction(() => axios.post("/championship/admin/reset"), "No se pudo reiniciar el campeonato.");
  };

  const renderMatchCard = (match, roundKey) => {
    const nameA = entryLabel(match.a, roundKeyById);
    const nameB = entryLabel(match.b, roundKeyById);
    const countdown = !match.winner && !match.live ? formatCountdown(match.scheduledAt, now) : "";
    const hasReplay = Boolean(match.replayId || match.historyIds?.length);
    const clickable = match.live || hasReplay;
    return (
      <div
        key={match.id}
        role={clickable ? "button" : undefined}
        tabIndex={clickable ? 0 : undefined}
        onClick={clickable ? () => handleMatchClick(match) : undefined}
        onKeyDown={clickable ? (event) => event.key === "Enter" && handleMatchClick(match) : undefined}
        className={`xc-match${match.winner ? " is-done" : ""}${roundKey === "final" ? " is-final" : ""}${
          match.live ? " is-live" : ""
        }${clickable ? " is-clickable" : ""}`}
        title={match.live ? "Ver en vivo" : hasReplay ? "Ver repetición" : undefined}
      >
        <span className={match.winner === "a" ? "is-winner" : match.winner === "b" ? "is-loser" : ""}>{nameA}</span>
        <small>VS</small>
        <span className={match.winner === "b" ? "is-winner" : match.winner === "a" ? "is-loser" : ""}>{nameB}</span>
        <em>{formatSchedule(match.scheduledAt)}</em>
        {countdown ? <b className="xc-countdown">{countdown}</b> : null}
        {match.live ? <b className="xc-live">● EN VIVO</b> : null}
        {!match.live && hasReplay ? <b className="xc-replay">▶ Repetición</b> : null}
      </div>
    );
  };

  const calendarRounds = roundsTopDown.map((round) => ({
    ...round,
    matches: round.matches.filter((match) => calendarTab === "all" || !match.winner),
  }));

  if (loading && !data) {
    return <div className="xc-page xc-loading">Cargando campeonato…</div>;
  }

  return (
    <div className="xc-page">
      <header className="xc-header">
        <IoTrophy className="xc-header-trophy" aria-hidden="true" />
        <div>
          <h1>{(data?.title || "Campeonato XIMA").toUpperCase()}</h1>
          <p>Demuestra tu habilidad y conviértete en el campeón</p>
        </div>
        {isAdmin ? (
          <button type="button" className="xc-admin-button" onClick={openSettings}>
            Ajustes del campeonato
          </button>
        ) : null}
      </header>

      {isAdmin && settingsOpen && draftSettings ? (
        <section className="xc-panel xc-settings">
          <label>
            Nombre
            <input
              value={draftSettings.title}
              onChange={(event) => setDraftSettings({ ...draftSettings, title: event.target.value })}
            />
          </label>
          <label className="xc-settings-prize">
            Premio (texto libre)
            <input
              value={draftSettings.prize}
              maxLength={300}
              placeholder="Ej.: 100 USD, 5.000 monedas, trofeo…"
              onChange={(event) => setDraftSettings({ ...draftSettings, prize: event.target.value })}
            />
          </label>
          <label>
            Modo de juego
            <select
              value={draftSettings.gameMode}
              onChange={(event) => setDraftSettings({ ...draftSettings, gameMode: event.target.value })}
            >
              {Object.entries(MODE_LABELS).map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
          </label>
          <label>
            Tiempo (min)
            <select
              value={draftSettings.timeMinutes}
              onChange={(event) => setDraftSettings({ ...draftSettings, timeMinutes: Number(event.target.value) })}
            >
              {TIME_OPTIONS.map((minutes) => (
                <option key={minutes} value={minutes}>
                  {minutes} min
                </option>
              ))}
            </select>
          </label>
          <label>
            Máx. jugadores
            <input
              type="number"
              min="2"
              max="16"
              value={draftSettings.maxPlayers}
              onChange={(event) => setDraftSettings({ ...draftSettings, maxPlayers: event.target.value })}
            />
          </label>
          <label>
            Estado
            <select
              value={draftSettings.status}
              onChange={(event) => setDraftSettings({ ...draftSettings, status: event.target.value })}
            >
              <option value="registration">Inscripción abierta</option>
              <option value="running">En juego (cierra inscripción)</option>
              <option value="finished">Terminado</option>
            </select>
          </label>
          <div className="xc-settings-actions">
            <button type="button" className="xc-primary" disabled={busy} onClick={saveSettings}>
              Guardar
            </button>
            <button type="button" className="xc-danger" disabled={busy} onClick={resetChampionship}>
              Reiniciar campeonato
            </button>
          </div>
        </section>
      ) : null}

      <div className="xc-layout">
        <aside className="xc-panel xc-calendar">
          <div className="xc-panel-title">
            <IoCalendarOutline aria-hidden="true" />
            <span>CALENDARIO</span>
          </div>
          <div className="xc-tabs">
            <button
              type="button"
              className={calendarTab === "upcoming" ? "is-active" : ""}
              onClick={() => setCalendarTab("upcoming")}
            >
              PRÓXIMOS
            </button>
            <button
              type="button"
              className={calendarTab === "all" ? "is-active" : ""}
              onClick={() => setCalendarTab("all")}
            >
              TODOS
            </button>
          </div>
          {isAdmin ? <p className="xc-hint">Toca un partido para poner fecha, hora o ganador. Con el estado «En juego», la sala de cada partido se abre sola 10 min antes.</p> : null}
          <div className="xc-calendar-list">
            {calendarRounds.map((round) =>
              round.matches.length ? (
                <div key={round.key} className="xc-calendar-round">
                  <h3>{ROUND_TITLES[round.key] || round.key}</h3>
                  {round.matches.map((match) => {
                    const editing = editingMatchId === match.id;
                    const countdown = !match.winner && !match.live ? formatCountdown(match.scheduledAt, now) : "";
                    return (
                      <div key={match.id} className={`xc-calendar-item${editing ? " is-editing" : ""}`}>
                        <button
                          type="button"
                          className="xc-calendar-row"
                          onClick={() => startEditing(match)}
                          disabled={!isAdmin}
                        >
                          <span>
                            {entryLabel(match.a, roundKeyById)} vs {entryLabel(match.b, roundKeyById)}
                          </span>
                          <small>
                            {formatSchedule(match.scheduledAt)}
                            {countdown ? <b> · {countdown}</b> : null}
                            {match.winner ? <b> · Ganó {entryLabel(match[match.winner], roundKeyById)}</b> : null}
                            {match.live ? <b> · EN VIVO</b> : null}
                            {match.note ? <i> · {match.note}</i> : null}
                          </small>
                          <IoTimeOutline aria-hidden="true" />
                        </button>
                        {isMyMatch(match) && match.tournamentId ? (
                          <button
                            type="button"
                            className="xc-play-mine is-small"
                            disabled={busy}
                            onClick={() => enterMyMatch(match)}
                          >
                            ▶ Jugar mi partido
                          </button>
                        ) : null}
                        {match.historyIds?.length ? (
                          <div className="xc-replays">
                            {match.historyIds.map((historyId, index) => (
                              <button key={historyId} type="button" onClick={() => openReplay(historyId, match)}>
                                ▶ {match.historyIds.length > 1 ? `Partida ${index + 1}` : "Repetición"}
                              </button>
                            ))}
                          </div>
                        ) : null}
                        {editing ? (
                          <div className="xc-calendar-editor">
                            <input
                              type="datetime-local"
                              value={draftSchedule}
                              onChange={(event) => setDraftSchedule(event.target.value)}
                            />
                            <select value={draftWinner} onChange={(event) => setDraftWinner(event.target.value)}>
                              <option value="">Sin ganador todavía</option>
                              <option value="a">Ganó {entryLabel(match.a, roundKeyById)}</option>
                              <option value="b">Ganó {entryLabel(match.b, roundKeyById)}</option>
                            </select>
                            <div>
                              <button type="button" className="xc-primary" disabled={busy} onClick={() => saveMatch(match)}>
                                Guardar
                              </button>
                              <button type="button" onClick={() => setEditingMatchId("")}>
                                Cancelar
                              </button>
                            </div>
                          </div>
                        ) : null}
                      </div>
                    );
                  })}
                </div>
              ) : null,
            )}
          </div>
          <p className="xc-legend">OF: Octavos · CF: Cuartos · SF: Semifinales</p>
        </aside>

        <main className="xc-center">
          {myOpenMatch ? (
            <div className="xc-mine">
              <span>
                Tu partido: <b>{entryLabel(myOpenMatch.a, roundKeyById)}</b> vs{" "}
                <b>{entryLabel(myOpenMatch.b, roundKeyById)}</b> · {formatSchedule(myOpenMatch.scheduledAt)}
                {!myOpenMatch.tournamentId && myOpenMatch.scheduledAt
                  ? ` · la sala se abre 10 min antes (${formatCountdown(myOpenMatch.scheduledAt, now)})`
                  : ""}
              </span>
              {myOpenMatch.tournamentId ? (
                <button type="button" className="xc-play-mine" disabled={busy} onClick={() => enterMyMatch(myOpenMatch)}>
                  ▶ JUGAR MI PARTIDO
                </button>
              ) : null}
            </div>
          ) : null}
          <div className="xc-stats">
            <div>
              <IoGameControllerOutline aria-hidden="true" />
              <span>
                <small>MODO DE JUEGO</small>
                <strong>{MODE_LABELS[data?.gameMode] || "Ciencia"} · 1 vs 1</strong>
              </span>
            </div>
            <div>
              <IoPeopleOutline aria-hidden="true" />
              <span>
                <small>JUGADORES</small>
                <strong>
                  {players.length} / {data?.maxPlayers || 12}
                </strong>
              </span>
            </div>
            <div>
              <IoTimeOutline aria-hidden="true" />
              <span>
                <small>TIEMPO DE JUEGO</small>
                <strong>{data?.timeMinutes || 5} MIN</strong>
              </span>
            </div>
          </div>

          <div className="xc-bracket" style={{ "--xc-seat-count": Math.max(2, seatEntries.length) }}>
            {roundsTopDown.map((round) => (
              <div key={round.key} className="xc-round">
                <div className="xc-round-title">{ROUND_TITLES[round.key] || round.key}</div>
                <div className="xc-round-row">
                  {round.matches.map((match) => (
                    <div key={match.id} className="xc-cell">
                      {renderMatchCard(match, round.key)}
                    </div>
                  ))}
                </div>
              </div>
            ))}
            <div className="xc-seats">
              {seatEntries.map((entry, index) => (
                <div
                  key={`seat-${index}`}
                  className={`xc-seat${entry?.kind === "bot" ? " is-bot" : ""}${entry?.kind === "open" ? " is-open" : ""}`}
                >
                  <b>{entry?.kind === "bot" ? "BOT" : entry?.seat}</b>
                  <PlayerAvatar entry={entry} />
                  <span>{entryLabel(entry, roundKeyById)}</span>
                  <small>{entry?.kind === "player" ? entry.points.toLocaleString("es-ES") : ""}</small>
                </div>
              ))}
            </div>
          </div>
          <p className="xc-footer">¡Gana cada partida, suma puntos XIMA y conviértete en el campeón!</p>
        </main>

        <aside className="xc-panel xc-players">
          <div className="xc-prize">
            <IoGiftOutline aria-hidden="true" />
            <span>
              <small>PREMIO DEL CAMPEONATO</small>
              <strong>{data?.prize || "Por anunciar"}</strong>
            </span>
          </div>
          <div className="xc-panel-title">
            <IoPersonOutline aria-hidden="true" />
            <span>
              JUGADORES ({players.length}/{data?.maxPlayers || 12})
            </span>
          </div>
          <div className="xc-voice-bar">
            <button
              type="button"
              className={live.listening ? "is-on" : ""}
              onClick={live.toggleListening}
              title={live.listening ? "Dejar de escuchar a todos" : "Escuchar a todos"}
            >
              {live.listening ? <IoVolumeHighOutline /> : <IoVolumeMuteOutline />}
              <span>{live.listening ? "Escuchando" : "Escuchar"}</span>
            </button>
            {live.canSpeak || live.adminMutedMe ? (
              <button
                type="button"
                className={live.micActive ? "is-on" : live.adminMutedMe ? "is-blocked" : ""}
                onClick={live.toggleMic}
                title={live.adminMutedMe ? "El administrador silenció tu micrófono" : "Mi micrófono"}
              >
                {live.micActive ? <IoMicOutline /> : <IoMicOffOutline />}
                <span>{live.micActive ? "Hablando" : live.adminMutedMe ? "Silenciado" : "Hablar"}</span>
              </button>
            ) : null}
            {live.status ? <small>{live.status}</small> : null}
          </div>
          <div className="xc-players-list">
            {players.length ? (
              players.map((player) => {
                const voiceState = live.voice[player.userId] || {};
                const isMe = player.userId === live.me;
                const listenOn = live.listening && !live.listenMuted[player.userId];
                const micOn = isMe ? live.micActive : voiceState.micActive === true;
                return (
                  <div
                    key={player.userId}
                    className={`xc-player-row${live.speaking[player.userId] ? " is-speaking" : ""}`}
                  >
                    <span className="xc-player-seat">{player.seat}</span>
                    <PlayerAvatar entry={player} size="sm" />
                    <span className="xc-player-name">
                      {player.name}
                      <small>{player.points.toLocaleString("es-ES")} pts</small>
                    </span>
                    <button
                      type="button"
                      className={`xc-voice-btn${listenOn ? " is-on" : ""}`}
                      onClick={() => live.toggleListenTo(player.userId)}
                      disabled={isMe}
                      aria-label={listenOn ? `Dejar de escuchar a ${player.name}` : `Escuchar a ${player.name}`}
                      title={isMe ? "Eres tú" : listenOn ? "Escuchando" : "Escuchar"}
                    >
                      {listenOn ? <IoVolumeHighOutline /> : <IoVolumeMuteOutline />}
                    </button>
                    <button
                      type="button"
                      className={`xc-voice-btn${micOn ? " is-on" : ""}${voiceState.adminMuted ? " is-blocked" : ""}`}
                      onClick={() => {
                        if (isMe) live.toggleMic();
                        else if (live.isAdmin) live.setAdminMute(player.userId, !voiceState.adminMuted);
                      }}
                      disabled={!isMe && !live.isAdmin}
                      aria-label={
                        isMe
                          ? "Mi micrófono"
                          : live.isAdmin
                          ? voiceState.adminMuted
                            ? `Permitir hablar a ${player.name}`
                            : `Silenciar a ${player.name}`
                          : `Micrófono de ${player.name}`
                      }
                      title={
                        voiceState.adminMuted
                          ? live.isAdmin && !isMe
                            ? "Silenciado por ti: toca para permitirle hablar"
                            : "Silenciado por el administrador"
                          : isMe
                          ? "Hablar"
                          : live.isAdmin
                          ? "Toca para silenciarlo"
                          : micOn
                          ? "Micrófono abierto"
                          : "Micrófono cerrado"
                      }
                    >
                      {micOn ? <IoMicOutline /> : <IoMicOffOutline />}
                    </button>
                  </div>
                );
              })
            ) : (
              <p className="xc-empty">Todavía no hay jugadores. ¡Sé el primero!</p>
            )}
          </div>
          {isRegistration ? (
            data?.joined ? (
              <button
                type="button"
                className="xc-join is-leave"
                disabled={busy}
                onClick={() =>
                  runAction(() => axios.post("/championship/leave"), "No se pudo salir.").then(live.refresh)
                }
              >
                Salir del campeonato
              </button>
            ) : (
              <button
                type="button"
                className="xc-join"
                disabled={busy || players.length >= (data?.maxPlayers || 12)}
                onClick={() =>
                  runAction(() => axios.post("/championship/join"), "No se pudo unir.").then(live.refresh)
                }
              >
                {players.length >= (data?.maxPlayers || 12) ? "Campeonato lleno" : "UNIRME"}
              </button>
            )
          ) : (
            <p className="xc-empty">La inscripción está cerrada.</p>
          )}
          <ChampionshipChat messages={live.chat} canChat={live.canChat} onSend={live.sendChat} />
        </aside>
      </div>
    </div>
  );
};

export default Championship;
